"use client";

import { useEffect, useRef } from "react";
import { CheckCircle2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { CONTACT } from "@/lib/constants/contact";
import type { ContactVariant } from "@/lib/validators/contact-form";

interface ContactSuccessPanelProps {
  variant: ContactVariant;
  onReset: () => void;
}

/**
 * Replaces the form on success rather than appending a banner beneath it.
 *
 * An emptied form is indistinguishable from an unsent one, which is what the
 * previous inline banner left on screen. Replacing the form removes the
 * ambiguity. A screen-reader user learns the submit worked because focus moves
 * to the heading on mount and the heading is `aria-describedby` the body copy,
 * so the confirmation and the what-happens-next detail are announced together —
 * previously they got silence.
 */
export function ContactSuccessPanel({
  variant,
  onReset,
}: ContactSuccessPanelProps) {
  const t = useTranslations("contactForm");
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex flex-col items-start gap-4 rounded-2xl border border-yellow-500/30 bg-site-card p-6 sm:p-8">
      <CheckCircle2 aria-hidden="true" className="h-10 w-10 text-yellow-500" />

      <h2
        ref={headingRef}
        tabIndex={-1}
        aria-describedby="contact-success-body"
        className="text-2xl font-semibold text-site-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
      >
        {variant === "careers"
          ? t("successHeadingJob")
          : t("successHeadingService")}
      </h2>

      <div
        id="contact-success-body"
        className="flex flex-col items-start gap-4"
      >
        <p className="text-site-foreground/80">
          {variant === "careers" ? t("successJob") : t("successService")}
        </p>
        {/*
          Two business hours is what the shop answers a service request in.
          Nobody has committed to it for hiring, and the job confirmation above
          already says HR will review and reply — so the promise is not repeated
          on a path where it would be a claim we invented.
        */}
        {variant !== "careers" && (
          <p className="text-site-foreground/80">{t("successResponseTime")}</p>
        )}

        <p className="text-site-foreground/80">
          {t.rich("successUrgent", {
            phone: () => <span data-phone="primary">{CONTACT.phoneWithCountry}</span>,
          })}
        </p>
      </div>

      <Button
        type="button"
        onClick={onReset}
        className="mt-2 h-12 rounded-full bg-yellow-500 px-6 font-medium text-black transition-colors hover:bg-yellow-600 motion-reduce:transition-none"
      >
        {t("sendAnother")}
      </Button>
    </div>
  );
}
